import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <DeveloperBoardIcon sx={{ fontSize: 80, color: '#0d3b66' }} />,
      title: 'Software Solutions',
      description: 'We are committed to delivering customized software solutions tailored to your unique needs. Additionally, we offer troubleshooting services and can recommend third-party software solutions when appropriate.',
      tagline: 'With the right tool for every task, we\'re here to help you identify and implement the perfect solution for your requirements.',
      features: [
        'Custom software development',
        'Software troubleshooting and support',
        'Third-party software recommendations',
        'System integration solutions',
        'Ongoing maintenance and updates',
      ]
    },
    {
      icon: <BuildIcon sx={{ fontSize: 80, color: '#0d3b66' }} />,
      title: 'Hardware Support',
      description: 'We are passionate about hardware! Whether you need assistance with getting your computer up and running, troubleshooting a phone that won\'t turn on, or simply figuring out where to begin, we are here to guide you.',
      tagline: 'Our goal is to find the smartest and most effective solution tailored to your needs.',
      features: [
        'Computer setup and configuration',
        'Hardware troubleshooting and repair',
        'Phone and tablet support',
        'Network equipment installation',
        'Hardware upgrade recommendations',
      ]
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 80, color: '#0d3b66' }} />,
      title: 'Training & Education',
      description: 'With years of experience, we specialize in guiding customers toward effective and satisfactory solutions. Whether you need assistance as an individual or require training and education for larger groups, we are here to help.',
      tagline: 'One of our greatest passions is empowering individuals to realize their full potential and achieve their goals!',
      features: [
        'Individual technology coaching',
        'Group training sessions',
        'Corporate technology workshops',
        'Software training programs',
        'Cybersecurity awareness training',
      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="h2" component="h1" fontWeight="700" gutterBottom>
          Our Services
        </Typography>
        <Typography variant="h5" color="text.secondary">
          Comprehensive technology solutions for individuals and businesses
        </Typography>
      </Box>

      {/* Services */}
      {services.map((service, index) => (
        <Paper
          key={index}
          elevation={3}
          sx={{
            mb: 6,
            overflow: 'hidden',
            transition: 'box-shadow 0.3s',
            '&:hover': { boxShadow: 8 }
          }}
        >
          <Grid container>
            {/* Content */}
            <Grid item xs={12} md={8}>
              <Box sx={{ p: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  {service.icon}
                  <Typography
                    variant="h3"
                    component="h2"
                    fontWeight="700"
                    sx={{ ml: 2 }}
                  >
                    {service.title}
                  </Typography>
                </Box>

                <Typography variant="h6" color="text.secondary" paragraph>
                  {service.description}
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#f8f9fa',
                    p: 3,
                    mb: 3,
                    borderLeft: '4px solid #d4af37'
                  }}
                >
                  <Typography variant="body1" fontWeight="600">
                    {service.tagline}
                  </Typography>
                </Paper>

                <Typography variant="h6" fontWeight="600" gutterBottom>
                  What We Offer:
                </Typography>
                <List>
                  {service.features.map((feature, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleIcon sx={{ color: '#d4af37' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>

            {/* CTA Panel */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #0d3b66 0%, #1a5490 100%)',
                  color: 'white',
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                <Typography variant="h5" fontWeight="600" gutterBottom>
                  Ready to Get Started?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                  Book a consultation and let's discuss how we can help you.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/about')}
                  sx={{
                    backgroundColor: '#d4af37',
                    '&:hover': { backgroundColor: '#b8941f' },
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    mb: 2
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.location.href = 'tel:561-344-4801'}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: '#d4af37',
                      backgroundColor: 'rgba(212, 175, 55, 0.1)'
                    },
                    fontWeight: 600,
                    px: 4,
                    py: 1.5
                  }}
                >
                  Call Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* Bottom CTA */}
      <Paper
        elevation={3}
        sx={{
          p: 6,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
        }}
      >
        <Typography variant="h4" fontWeight="700" gutterBottom>
          Need Help Deciding?
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          We're here to help you find the right solution for your needs
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/about')}
            sx={{
              backgroundColor: '#0d3b66',
              '&:hover': { backgroundColor: '#1a5490' },
              fontWeight: 600,
              px: 6,
              py: 1.5
            }}
          >
            Schedule Consultation
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => window.location.href = 'mailto:mikel.smart@smartindustriesllc.com'}
            sx={{
              borderColor: '#0d3b66',
              color: '#0d3b66',
              '&:hover': {
                borderColor: '#1a5490',
                backgroundColor: 'rgba(13, 59, 102, 0.05)'
              },
              fontWeight: 600,
              px: 6,
              py: 1.5
            }}
          >
            Send Email
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Services;
